import { Link } from "@tanstack/react-router";
import type { ComponentProps, ReactNode } from "react";
import { CopyButton } from "@/components/shared/copy-button";
import { cn } from "@/lib/utils";

interface SectionBarProps {
  title: ReactNode;
  subtitle?: ReactNode;
  linkTo?: ComponentProps<typeof Link>["to"];
  linkParams?: ComponentProps<typeof Link>["params"];
  // Optional right-side slot for interactive controls (e.g. <FavoriteToggle />).
  // Rendered outside the link wrapper so its clicks stay local and don't
  // trigger the section's navigation. When both `linkTo` and `rightSlot` are
  // set, the link covers only the title text; the right slot stays clickable.
  rightSlot?: ReactNode;
  // When set, renders a small copy-to-clipboard icon button next to the
  // title. The string is what lands on the clipboard (raw policy/claim/agency
  // number, no "Policy No." prefix). `copyLabel` controls the toast text.
  copyValue?: string;
  copyLabel?: string;
  className?: string;
}

export function SectionBar({
  title,
  subtitle,
  linkTo,
  linkParams,
  rightSlot,
  copyValue,
  copyLabel,
  className,
}: SectionBarProps) {
  // Title is clickable when linkTo is set. Underline-on-hover replaces the
  // old up-arrow icon — same affordance, less visual noise, and keeps the
  // single nav path (header click) consistent across dashboards.
  const titleSide = linkTo ? (
    <Link
      to={linkTo}
      params={linkParams}
      className="flex min-w-0 items-baseline gap-3 truncate font-semibold text-sm hover:underline"
    >
      <span className="truncate">{title}</span>
      {subtitle && (
        <span className="truncate font-normal text-primary-foreground/80 text-xs">{subtitle}</span>
      )}
    </Link>
  ) : (
    <div className="flex min-w-0 items-baseline gap-3">
      <span className="truncate font-semibold text-sm">{title}</span>
      {subtitle && <span className="truncate text-primary-foreground/80 text-xs">{subtitle}</span>}
    </div>
  );

  const leftSide = copyValue ? (
    <div className="flex min-w-0 flex-1 items-center gap-1">
      {titleSide}
      <CopyButton value={copyValue} label={copyLabel} inverse className="size-5" />
    </div>
  ) : (
    <div className="flex min-w-0 flex-1">{titleSide}</div>
  );

  return (
    <div
      className={cn(
        "flex items-center justify-between gap-2 rounded-t-md bg-primary px-3 py-2 text-primary-foreground",
        className,
      )}
    >
      {leftSide}
      {rightSlot && <div className="flex shrink-0 items-center">{rightSlot}</div>}
    </div>
  );
}
